import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import template from './formatting-form.hbs'
import dropdown from 'scoreboard/components/dropdown'
import EditCellOptions from './edit-cell-options'
import reportConstants from 'scoreboard/reports/report-constants'
import StackForm from './stack-form'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import ThresholdColorsDialog from 'scoreboard/components/threshold-colors/threshold-colors-dialog'
import ThresholdColorModel from 'scoreboard/components/threshold-colors/threshold-colors-model'
import appDisplay from 'scoreboard/components/app-display'
import isNumericalDataType from 'scoreboard/components/is-numeric-data-type'
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'

const FormattingForm = StackForm.extend({

	className: 'edit-stack report',

	events: {
		'click button[data-style]': '_updateStyle',
		'click button[data-justified]': '_updateJustified',
		'change select.font-size-dropdown' : '_onChangeFontSize',
		'change select.color-type-dropdown' : '_updateColorType',
		'change select.color-style-dropdown' : '_updateCellFill',
		'change select.precision-dropdown' : '_updatePrecision',
		'click .edit-condition-color' : '_editConditionalColor'
	},

	/**
	 * View for editing the formatting of a stack of a report report
	 *
	 * @param {ReportStack} options.model the Report Stack
	 * @param {ReportModel} options.reportModel the model of the report
	 * @param {ReportTableModel} options.tableModel the model of the table
	 * @param {ReportColumn} options.column the model of one of a column in a stack
	 */
	initialize: function(options = {}) {
		StackForm.prototype.initialize.apply(this, arguments)
	},

	render: function() {
		let {attributes} = this.column
		this.$el.html(template(this.options))

		let tableNode = this.$('.table')[0]
		let tableOpts = {editable: false}
		let tableModel = new this.tableModel.constructor()

		//render the report table to show the stack being edited and its data
		this.table = tableModel.build(this.reportModel, tableNode, tableOpts)

		//exclude the id from setting numerical options even though ids are
		//numerical, the id itself is not user data and formatting it
		//numerically is not useful
		let dataType = attributes.dataType
		let isNumerical = isNumericalDataType(dataType) ||
			isNumericalDataType(this.column.resolveDataType(this.reportModel))
		if ((isNumerical && attributes.columnType != 'id') ||
			attributes.columnType === 'expenditures') {
			this._prepNumericalOptions()
		} else {
			this.$('.numerical-controls-container').addClass('is-not-displayed')
		}

		//set the initial justified state if any
		if (!_.isEmpty(attributes.justified)) {
			this.$(`button[data-justified=${attributes.justified}]`)
				.addClass('is-selected')
		}

		this.fontSizeDropdown = this.$('.font-size-dropdown')
		dropdown.idLabels(this.fontSizeDropdown.empty(), EditCellOptions.fontSize,
			attributes.fontSize || 'medium')

		this.colorTypeDropdown = this.$('.color-type-dropdown')

		let removeScoringColor = !this.column.isScored()
		// #57089
		// Color columns from initiative reports need to select a different
		// default color type
		let isOverallColorColumn =
			attributes.columnType === 'overall-color'
		let isNewOverallColorColumn =
			!attributes.colorType && isOverallColorColumn
		if (isNewOverallColorColumn) {
			attributes.colorType = 'overall'
		}
		dropdown.idLabels(this.colorTypeDropdown.empty(),
			EditCellOptions.colorType(
				removeScoringColor,
				!isNumerical,
				!isOverallColorColumn
			), attributes.colorType || 'regular')

		this.cellFillDropdown = this.$('.color-style-dropdown')
		// #57147
		// Default color style should be triangle for all report color columns
		const colorStyleValue = attributes.colorStyle ?? 'triangle'
		dropdown.idLabels(this.cellFillDropdown.empty(), EditCellOptions.colorStyle(),
			colorStyleValue)
		this._updateColumnAttribute('colorStyle', colorStyleValue)

		//handle the various logic regarding color values
		this._updateColorControls()

		this._writeFontStyleActiveStates()

		this._maybeRenderRollupTreeKeyToggle()

		return StackForm.prototype.render.apply(this, arguments)
	},

	_writeFontStyleActiveStates: function() {
		[ 'bold', 'italic', 'underlined' ].forEach(styleType => {
			this.$(`button[data-style="${styleType}"]`)
				.toggleClass('is-selected', this.column.get(styleType))
		})
	},

	_prepNumericalOptions: function() {
		let {attributes} = this.column
		let checked = !!attributes.abbreviated

		let precisionContainer = this.$('.precision-container')
			.toggleClass('is-not-displayed', !checked)
		this.abbreviateToggle = new OnOffToggle({
			name: 'abbreviated',
			label: i18n.get('common.abbreviate'),
			checked
		})
		this.listenTo(this.abbreviateToggle, 'change', abbreviated => {
			this._updateColumnAttribute('abbreviated', abbreviated)
			precisionContainer.toggleClass('is-not-displayed', abbreviated)
		})
		precisionContainer.toggleClass('is-not-displayed',
			this.column.get('abbreviated'))
		this.$('.abbreviate-toggle').append(
			this.abbreviateToggle.el)
		this.abbreviateToggle.render()

		dropdown.precision(this.$('.precision-dropdown'),
			attributes.precision)
	},

	_maybeRenderRollupTreeKeyToggle: function() {
		if (this.reportModel.reportDomainType == 'dataset') {
			const field = this.reportModel.getDatasetField(this.column)
			if (field && isGeographicOrRollup(field.dataType)) {
				this.rollupKeysToggle = new OnOffToggle({
					name: 'showRollupKeys',
					label: i18n.get('datasets.field.showKeys'),
					checked: this.column.get('showRollupKeys')
				})
				this.listenTo(this.rollupKeysToggle, 'change', show => {
					this._updateColumnAttribute('showRollupKeys', show)
				})
				this.$('.tree-keys-toggle').append(this.rollupKeysToggle.el)
				this.rollupKeysToggle.render()
			}
		}
	},

	_editConditionalColor: function(e) {
		let colorModel = this.column.colorModel()
		if (colorModel == null) {
			colorModel = new ThresholdColorModel({
				min: this.column.get('min'),
				max: this.column.get('max')
			})
		}

		let view = new ThresholdColorsDialog({
			model: colorModel,
			title: i18n.get('report.designer.conditionalColors')
		})
		view.on('done', model => {
			this._updateColumnAttribute('thresholdColoring', model.attributes)
		})
		appDisplay.dialog(view)
	},

	_updateStyle: function(e) {
		let target = this.$(e.target).toggleClass('is-selected')
		let styleType = target.data('style')
		this._updateColumnAttribute(styleType, !this.column.get(styleType))
	},

	_updateJustified: function(e) {
		this.$('button[data-justified]').removeClass('is-selected')
		let target = this.$(e.target).toggleClass('is-selected')
		this._updateColumnAttribute('justified', target.data('justified'))
	},

	_updateColorType: function(e) {
		let colorType = this.$('.color-type-dropdown').val()
		this._updateColorControls()
		this._updateColumnAttribute('colorType', colorType)
	},

	_updateCellFill: function(e) {
		this._updateColumnAttribute('colorStyle', this.$('.color-style-dropdown').val())
	},

	_updatePrecision: function(e) {
		let val = this.$('.precision-dropdown').val()
		if (val != '') {
			val = _.toFinite(val)
		}
		this._updateColumnAttribute('precision', val)
	},

	_onChangeFontSize: function(e) {
		this._updateColumnAttribute('fontSize', this.$(e.target).val())
	},

	_updateColumnAttribute: function(key, value, opts = {}) {
		//ensure the column on the model is the one we're updating
		if (value === '') {
			//reset back to default
			this.column.unset(key)
		} else {
			this.column.set(key, value)
		}
	},

	_updateColorControls: function() {
		let colorType = this.$('.color-type-dropdown').val()
		let isScoring = colorType == reportConstants.COLOR_TYPE_ID_SCORING
		let isConditional = colorType == reportConstants.COLOR_TYPE_ID_CONDITIONAL
		let isOverall = colorType == reportConstants.COLOR_TYPE_ID_OVERALL
		this.$('.color-style-container').toggleClass('is-not-displayed',
			!isScoring && !isConditional && !isOverall)

		this.$('.conditional-color-container').toggleClass('is-not-displayed',
			!isConditional)
	},

	remove: function() {
		StackForm.prototype.remove.apply(this, arguments)
		_.invokeMap([this.abbreviateToggle, this.table, this.rollupKeysToggle],
			'remove')
	}
})

export default FormattingForm
FormattingForm.__filename__ = 'reports/common/formatting-form.js'
